import { useCallback } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import type { StatusTransition } from '@atlassian/jira-issue-field-status/src/common/types.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { UseFetchTransitionsService } from './types.tsx';
import type { TransitionResponse } from './utils/transformer.tsx';
import getTransitions from './utils/client.tsx';

type FetchTransitionsPromise = (payload: {
	projectId: string;
	issueTypeId: string;
	selectedCloudId?: string;
	expand?: string;
}) => Promise<StatusTransition[] | TransitionResponse | undefined>;

const useFetchTransitionsService: UseFetchTransitionsService = () => {
	const { cloudId } = useTenantContext();

	const fetchTransitions: FetchTransitionsPromise = useCallback(
		({ projectId, issueTypeId, selectedCloudId, expand }) =>
			getTransitions(selectedCloudId || cloudId, projectId, issueTypeId, expand),
		[cloudId],
	);
	const { loading, error, data, fetch } = useService(fetchTransitions);

	// Extract transitions and fieldRequiredValidators from the response
	const transitions = data && Array.isArray(data) ? data : data?.transitions;
	const fieldRequiredValidators =
		data && !Array.isArray(data) ? data.fieldRequiredValidators : undefined;

	return [
		{
			isLoading: loading,
			error,
			transitions,
			fieldRequiredValidators,
		},
		fetch,
	];
};

export default useFetchTransitionsService;
